import { defineComponent as _defineComponent } from 'vue'
interface Tab {
  key: string
  el: HTMLElement
}

import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
  OzPlainButtonHrefMode as OzTabsWithSeparatorHrefMode,
} from '@@/library/v4/components/OzPlainButton.vue'
import tinykeys from 'tinykeys'
import type { ComponentPublicInstance } from 'vue'
import { computed, defineProps, onBeforeUnmount, onMounted, ref, withDefaults } from 'vue'

interface OzTabsWithSeparatorButtonProps {
  text: string
  key: string
  ariaControls?: string
  href?: string
  hrefMode?: OzTabsWithSeparatorHrefMode
  disabled?: boolean
  /**
   * Id for querying tab in tests
   */
  testId?: string
  colorPreset?: OzTabsWithSeparatorButtonColorPreset
}

enum OzTabsWithSeparatorAccessibilityRole {
  Tabs = 'Tabs',
  Navigation = 'Navigation',
}

enum OzTabsWithSeparatorSizePreset {
  H28px = 'H28px',
  H37px = 'H37px',
  H40px = 'H40px',
  H42px = 'H42px',
  H48px = 'H48px',
  H59px = 'H59px',
}

enum OzTabsWithSeparatorButtonColorPreset {
  Primary = 'Primary',
}

export {
  OzTabsWithSeparatorAccessibilityRole,
  OzTabsWithSeparatorButtonColorPreset,
  OzTabsWithSeparatorHrefMode,
  OzTabsWithSeparatorSizePreset,
}
export type { OzTabsWithSeparatorButtonProps }
const __default__ = {}

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'OzTabsWithSeparator',
  props: {
    ariaLabel: null,
    tabs: null,
    currentTabKey: { default: undefined },
    sizePreset: { default: OzTabsWithSeparatorSizePreset.H42px },
    accessibilityRole: { default: OzTabsWithSeparatorAccessibilityRole.Tabs },
    darkMode: { type: [Boolean, String], default: 'auto' },
    tabsContainerClasses: { default: undefined },
    nonDisabledButtonClasses: { default: undefined }
  },
  emits: ["activate-tab"],
  setup(__props: any, { emit }: { emit: ({
  (
    e: 'activate-tab',
    event: Event,
    tab: {
      key: string
    },
  ): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { ariaLabel: string, tabs: OzTabsWithSeparatorButtonProps[], currentTabKey: string, sizePreset: OzTabsWithSeparatorSizePreset, accessibilityRole: OzTabsWithSeparatorAccessibilityRole, darkMode: boolean | 'auto', tabsContainerClasses: string | undefined, nonDisabledButtonClasses: string | undefined }

// Defining props




const list = ref<HTMLUListElement>()
const currentTabId = computed(() => props.currentTabKey || props.tabs[0].key)
const tabsRef = ref<Array<Tab | null>>(Array(props.tabs.length).fill(null))

const setTabRef = (
  el: Element | ComponentPublicInstance | null,
  index: number,
  tab: OzTabsWithSeparatorButtonProps,
) => {
  if (el === null) return
  tabsRef.value[index] = { key: tab.key, el: el as HTMLElement }
}

const wrapperElement = computed(() => {
  return props.accessibilityRole === OzTabsWithSeparatorAccessibilityRole.Tabs ? 'div' : 'nav'
})

const wrapperAttributes = computed(() => {
  return props.accessibilityRole === OzTabsWithSeparatorAccessibilityRole.Tabs ? {} : { 'aria-label': props.ariaLabel }
})

const listAttributes = computed(() => {
  return isUsingTabsAccessibilityRole.value ? { 'aria-label': props.ariaLabel } : {}
})

const listRole = computed(() => {
  return isUsingTabsAccessibilityRole.value ? 'tablist' : 'list'
})

const listItemRole = computed(() => {
  return isUsingTabsAccessibilityRole.value ? 'presentation' : 'listitem'
})

const isUsingTabsAccessibilityRole = computed(() => {
  return props.accessibilityRole === OzTabsWithSeparatorAccessibilityRole.Tabs
})

function computeTabAttributes(tab: OzTabsWithSeparatorButtonProps): Record<string, any> {
  return isUsingTabsAccessibilityRole.value
    ? {
        id: tab.key,
        role: 'tab',
        'aria-controls': tab.ariaControls,
        'aria-selected': currentTabId.value === tab.key ? 'true' : 'false',
        tabindex: currentTabId.value === tab.key ? 0 : -1,
      }
    : {
        role: tab.href ? 'link' : 'button',
      }
}

/**
 * Focus the first tab.
 */
function focusFirstTab(): void {
  if (tabsRef.value.length === 0) return
  const el = tabsRef.value[0]?.el.firstChild as HTMLElement
  el.focus()
}

/**
 * Focus the last tab.
 */
function focusLastTab(): void {
  if (tabsRef.value.length === 0) return
  const lastTabEl = tabsRef.value[tabsRef.value.length - 1]
  if (lastTabEl !== null) {
    const el = lastTabEl.el.firstChild as HTMLElement
    el.focus()
  }
}

/**
 * When pressing "Home", focus the first tab.
 */
function handleHomeKey(event: KeyboardEvent): void {
  event.preventDefault()
  focusFirstTab()
}

/**
 * When pressing "End", focus the last tab.
 */
function handleEndKey(event: KeyboardEvent): void {
  event.preventDefault()
  focusLastTab()
}

/**
 * When pressing the left arrow, focus the previous tab.
 * If on first tab, focus the last tab.
 */
function handleArrowLeft(event: KeyboardEvent): void {
  const target = event.target as HTMLElement
  const wrapper = target.parentElement

  // Skip the separator
  if (wrapper?.previousElementSibling?.previousElementSibling) {
    const previousSibling = wrapper.previousElementSibling.previousElementSibling.firstChild as HTMLElement
    previousSibling?.focus()
  } else {
    focusLastTab()
  }
}

/**
 * When pressing the right arrow, focus the next tab.
 * If on last tab, focus the first tab.
 */
function handleArrowRight(event: KeyboardEvent): void {
  const target = event.target as HTMLElement
  const wrapper = target.parentElement

  // Skip the separator
  if (wrapper?.nextElementSibling?.nextElementSibling) {
    const nextSibling = wrapper.nextElementSibling.nextElementSibling.firstChild as HTMLElement
    nextSibling?.focus()
  } else {
    focusFirstTab()
  }
}

let removeKeyboardShortcutsListener = () => {}

onMounted(() => {
  if (list.value) {
    removeKeyboardShortcutsListener = tinykeys(list.value, {
      Home: handleHomeKey,
      End: handleEndKey,
      ArrowLeft: handleArrowLeft,
      ArrowRight: handleArrowRight,
    })
  }
})

onBeforeUnmount(() => {
  removeKeyboardShortcutsListener()
})

return { __sfc: true,OzTabsWithSeparatorAccessibilityRole, OzTabsWithSeparatorSizePreset, OzTabsWithSeparatorButtonColorPreset, props, emit, list, currentTabId, tabsRef, setTabRef, wrapperElement, wrapperAttributes, listAttributes, listRole, listItemRole, isUsingTabsAccessibilityRole, computeTabAttributes, focusFirstTab, focusLastTab, handleHomeKey, handleEndKey, handleArrowLeft, handleArrowRight, removeKeyboardShortcutsListener, OzPlainButton, OzPlainButtonColorScheme, OzPlainButtonSizePreset }
}

})